import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

/**
 * Records which pages a visitor opens, so a customer record shows a real
 * timeline instead of "has not visited any pages yet".
 *
 * Before this, the only writer of `page_visits` was the live-chat widget, which
 * needed its iframe to have booted and the visitor to sit still for five
 * seconds — fifteen people in the whole database had any history. This fires on
 * the route change itself, from the app that is already running.
 *
 * Guests are recorded too: the server files the visit against the browser's own
 * id, and hands those rows to the account if that browser signs up — so a
 * record can show what they were reading before they joined.
 *
 * Not recorded: the staff areas — a colleague working in /dashboard is not a
 * customer browsing the site. The server refuses those paths again and strips
 * token-bearing query parameters, so this is belt and braces.
 */
const SKIP = /^\/(admin|dashboard|staff)(\/|$)/;

export function usePageViewTracking() {
  const location = useLocation();
  const lastSent = useRef<string>('');

  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    if (SKIP.test(location.pathname) || path === lastSent.current) return;
    lastSent.current = path;

    // The title is set by the page that just mounted, a tick later than this
    // effect — wait for it so the timeline reads as names, not paths.
    const timer = setTimeout(() => {
      apiClient
        .post('track/page-view', {
          url: path,
          title: document.title || null,
          referrer: document.referrer || null,
        })
        .catch(() => {});
    }, 1200);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // Closing the tab leaves the last page open-ended; stamp it on the way out.
  useEffect(() => {
    const close = () => {
      if (!lastSent.current) return;
      const {base_url} = getBootstrapData().settings;
      try {
        fetch(`${base_url}/api/v1/track/page-view/end`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-TOKEN': getBootstrapData().csrf_token,
          },
          body: '{}',
        }).catch(() => {});
      } catch {
        // A browser that refuses a keepalive request simply leaves the row
        // open; the next page view closes it anyway.
      }
    };
    window.addEventListener('pagehide', close);
    return () => window.removeEventListener('pagehide', close);
  }, []);
}
